import React from 'react';
import {
  FileSearch,
  ClipboardCheck,
  Layers,
  Maximize2,
  Lock,
  Check,
} from 'lucide-react';
import { useInView } from '../../hooks/useInView';
import { servicesData } from '../../data/mock';

const iconMap = { FileSearch, ClipboardCheck, Layers, Maximize2, Lock };

const ServiceCard = ({ service, index }) => {
  const [ref, isInView] = useInView();
  const Icon = iconMap[service.iconName];

  return (
    <div
      ref={ref}
      className={`group relative p-7 rounded-xl border border-stone-200 bg-white hover:border-amber-300/60 hover:shadow-lg hover:shadow-amber-500/[0.04] transition-[opacity,transform,border-color,box-shadow] duration-500 ease-out ${
        isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className="w-11 h-11 rounded-lg bg-amber-50 border border-amber-100 flex items-center justify-center mb-5 transition-[background-color] duration-300 group-hover:bg-amber-100">
        {Icon && <Icon className="w-5 h-5 text-amber-600" />}
      </div>

      <h3 className="text-lg font-semibold text-stone-900 mb-2">
        {service.title}
      </h3>
      <p className="text-sm text-stone-500 leading-relaxed mb-5">
        {service.description}
      </p>

      <ul className="space-y-2.5">
        {service.features.map((feature) => (
          <li key={feature} className="flex items-start gap-2.5">
            <Check className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
            <span className="text-sm text-stone-600">{feature}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const ServicesSection = () => {
  const [titleRef, titleInView] = useInView();

  return (
    <section id="services" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div
          ref={titleRef}
          className={`text-center mb-16 transition-[opacity,transform] duration-700 ease-out ${
            titleInView
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-6'
          }`}
        >
          <span className="text-xs font-semibold tracking-widest uppercase text-amber-600 mb-3 block">
            What We Offer
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-4">
            Services Tailored to Your Needs
          </h2>
          <p className="text-stone-500 max-w-2xl mx-auto text-lg">
            From quick code reviews to comprehensive audits, we help your team
            write cleaner, more scalable Python code.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {servicesData.map((service, i) => (
            <ServiceCard key={service.title} service={service} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
